import { getTenantTokenForAppId } from "./jwt2json";
import { stripStoredUserTokenSuffix } from "./token";
import { ContactFormProps } from "./properties";

/** Must stay aligned with tva-api `MIN_PUBLIC_FORM_DURATION_MS`. */
export const MIN_GET_IN_TOUCH_FORM_DURATION_MS = 3000;

/** Must stay aligned with tva-api `MAX_PUBLIC_FORM_AGE_MS` — the server rejects an
 *  older `formOpenedAt` as `STALE_FORM`, so the client mirrors it to fail early with
 *  a clear message instead of letting the user submit into a server rejection. */
export const MAX_GET_IN_TOUCH_FORM_DURATION_MS = 60 * 60 * 1000;

export const MISSING_SITE_TOKEN_MESSAGE =
  "This form could not be submitted because the site identity token is missing. Please refresh and try again.";

export const GET_IN_TOUCH_FORM_INCOMPLETE_MESSAGE =
  "Please complete the form before submitting.";

export const GET_IN_TOUCH_TOO_FAST_MESSAGE =
  "Please take a moment to review your message before sending.";

export const GET_IN_TOUCH_STALE_FORM_MESSAGE =
  "This form has been open for a while. Please review your details and submit again.";

type BuildGetInTouchAuthHeadersDeps = {
  getTenantToken: () => string | null;
  stripSuffix: (token: string) => string;
};

const defaultAuthHeaderDeps: BuildGetInTouchAuthHeadersDeps = {
  // getTenantTokenForAppId returns string | undefined; normalise the "no token"
  // case to null so it satisfies the dep contract and the `if (!siteToken)` guard.
  getTenantToken: () => getTenantTokenForAppId() ?? null,
  stripSuffix: stripStoredUserTokenSuffix,
};

export function buildGetInTouchAuthHeaders(
  storedUserToken: string | undefined,
  deps: BuildGetInTouchAuthHeadersDeps = defaultAuthHeaderDeps
): Record<string, string> | null {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (storedUserToken && typeof storedUserToken === "string") {
    const cleanUserToken = deps.stripSuffix(storedUserToken);
    headers.Authorization = `Bearer ${cleanUserToken}`;
    headers["x-auth-token"] = cleanUserToken;
    return headers;
  }

  const siteToken = deps.getTenantToken();
  if (!siteToken) {
    return null;
  }

  headers.Authorization = `Bearer ${siteToken}`;
  return headers;
}

export function isGetInTouchFormReadyToSubmit(
  formOpenedAt: number | null,
  nowMs: number = Date.now()
): boolean {
  if (formOpenedAt === null) {
    return false;
  }

  return nowMs - formOpenedAt >= MIN_GET_IN_TOUCH_FORM_DURATION_MS;
}

/** True once the form has been open past the server's max age — submitting now would
 *  be rejected as `STALE_FORM`, so the session must be refreshed first. */
export function isGetInTouchFormStale(
  formOpenedAt: number | null,
  nowMs: number = Date.now()
): boolean {
  if (formOpenedAt === null) {
    return false;
  }

  // Strictly greater than, to mirror tva-api publicFormSpamGuard exactly
  // (`elapsedMs > MAX_PUBLIC_FORM_AGE_MS`): a submit at the exact boundary is still
  // valid server-side, so the client must not block it.
  return nowMs - formOpenedAt > MAX_GET_IN_TOUCH_FORM_DURATION_MS;
}

export function getGetInTouchTimingValidationError(
  formOpenedAt: number | null,
  nowMs: number = Date.now()
): string | null {
  if (formOpenedAt === null) {
    return GET_IN_TOUCH_FORM_INCOMPLETE_MESSAGE;
  }

  if (!isGetInTouchFormReadyToSubmit(formOpenedAt, nowMs)) {
    return GET_IN_TOUCH_TOO_FAST_MESSAGE;
  }

  if (isGetInTouchFormStale(formOpenedAt, nowMs)) {
    return GET_IN_TOUCH_STALE_FORM_MESSAGE;
  }

  return null;
}

export type GetInTouchTimingGuardState = {
  formOpenedAt: number | null;
  submitReady: boolean;
};

/** Clears timing after a completed submit so repeat sends need a fresh focus + 3s wait. */
export function resetGetInTouchTimingGuard(): GetInTouchTimingGuardState {
  return {
    formOpenedAt: null,
    submitReady: false,
  };
}

export type GetInTouchFormSessionReset = GetInTouchTimingGuardState & {
  companyWebsite: string;
};

/** Clears timing + honeypot after submit success or modal close (autofill recovery). */
export function resetGetInTouchFormSession(): GetInTouchFormSessionReset {
  return {
    ...resetGetInTouchTimingGuard(),
    companyWebsite: "",
  };
}

export type GetInTouchRequestPayload = ContactFormProps & {
  website: string;
  sourceUrl: string;
  companyWebsite: string;
  formOpenedAt: number;
};

export function buildGetInTouchRequestPayload(
  data: ContactFormProps,
  options: {
    websiteName: string;
    sourceUrl: string;
    companyWebsite: string;
    formOpenedAt: number;
  }
): GetInTouchRequestPayload {
  return {
    ...data,
    website: options.websiteName,
    sourceUrl: options.sourceUrl,
    companyWebsite: options.companyWebsite,
    formOpenedAt: options.formOpenedAt,
  };
}
